// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { CustomAppState } from "@hardwoods/App/Store/Reducers";
import { getLineNotes, getLineNotesHasError, getProductAdditionalInfos } from "@hardwoods/App/Store/Selectors/CustomProductDetails";
import { color } from "@hardwoods/XDL/Utilities";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import TextArea from "@insite/mobius/TextArea";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { useEffect } from "react";
import { connect, useDispatch } from "react-redux";
import { css } from "styled-components";

type Props = WidgetProps;

export interface ProductDetailsNoteLineStyle {
    wrapper?: InjectableCss;
    editableNotesTextArea?: InjectableCss;
}

export const noteLineStyles: ProductDetailsNoteLineStyle = {
    wrapper: {
        css: css`
            width: 100%;
            border-top: 1px solid ${color("common.border")};
            margin-top: 10px;
            padding-top: 5px;
        `,
    },
};

const styles = noteLineStyles;

const ProductDetailsNoteLine: React.FC<Props> = (props) => {
    const { xLineNotes, lineNotesHasError, productAdditionalInfos } = useAppSelector(state => {
        const xState = state as CustomAppState;
        return {
            xLineNotes: getLineNotes(xState),
            lineNotesHasError: getLineNotesHasError(xState),
            productAdditionalInfos: getProductAdditionalInfos(xState),
        };
    });

    const dispatch = useDispatch();
    const [lineNotes, setLineNotes] = React.useState("");

    useEffect(() => {
        dispatch({
            type: "Data/CustomProductDetails/ClearLineNotes",
        });
    }, []);

    useEffect(() => {
        if (!xLineNotes) {
            setLineNotes("");
        }
    }, [xLineNotes]);

    if (productAdditionalInfos?.showItemSpecField !== "True") {
        return null;
    }

    const changeHandler = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        const updatedLineNotes = event.currentTarget.value;
        setLineNotes(updatedLineNotes);
        dispatch({
            type: "Data/CustomProductDetails/ChangeLineNotes",
            lineNotes: updatedLineNotes,
        });
    };

    return (
        <StyledWrapper {...styles.wrapper} data-test-selector="productDetails_note_line">
            <TextArea
                {...styles.editableNotesTextArea}
                label={translate("Additional Requests")}
                value={lineNotes}
                placeholder={productAdditionalInfos?.itemSpecPlacholder}
                onChange={changeHandler}
                required={productAdditionalInfos?.itemSpecIsRequired === "True"}
                error={lineNotesHasError && !lineNotes ? translate("This field is required.") : ""}
                data-test-selector="cartline_notes"
            />
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: ProductDetailsNoteLine,
    definition: {
        displayName: "Product Note Line",
        group: "Xngage Custom" as any,
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
